import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Calendar, Users, FileText, MessageSquare, HelpCircle, Lightbulb, LogOut, Menu } from 'lucide-react';
import axios from 'axios';
import { DashboardHome } from './admin/DashboardHome';
import { StartupsManagement } from './admin/StartupsManagement';
import { EventsManagement } from './admin/EventsManagement';
import { AdvisorsManagement } from './admin/AdvisorsManagement';
import { RegistrationsManagement } from './admin/RegistrationsManagement';
import { BookingsManagement } from './admin/BookingsManagement';
import { QueriesManagement } from './admin/QueriesManagement';
import { IdeasManagement } from './admin/IdeasManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CfELogo = "https://customer-assets.emergentagent.com/job_startup-central-3/artifacts/3g256hrq_CfE%20Logo.png";

const sidebarItems = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Startups', path: '/admin/startups', icon: Building2 },
  { name: 'Events', path: '/admin/events', icon: Calendar },
  { name: 'Advisors', path: '/admin/advisors', icon: Users },
  { name: 'Registrations', path: '/admin/registrations', icon: FileText },
  { name: 'Bookings', path: '/admin/bookings', icon: MessageSquare },
  { name: 'Queries', path: '/admin/queries', icon: HelpCircle },
  { name: 'Ideas', path: '/admin/ideas', icon: Lightbulb },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyAuth = async () => {
      const token = sessionStorage.getItem('cfe_session_token');
      if (!token) { navigate('/admin/login'); return; }
      try {
        const response = await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
        setAdmin(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        sessionStorage.removeItem('cfe_session_token');
        navigate('/admin/login');
      } finally { setLoading(false); }
    };
    verifyAuth();
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('cfe_session_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#075FA3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#58595B]">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex">
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-gray-100">
            <img src={CfELogo} alt="CfE" className="h-80" />
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {sidebarItems.map((item) => (
              <Link key={item.path} to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${location.pathname === item.path ? 'bg-[#075FA3] text-white' : 'text-[#58595B] hover:bg-gray-100'}`}>
                <item.icon className="w-5 h-5" />{item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#C83041] hover:bg-red-50 w-full transition-colors" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />Logout
            </button>
          </div>
        </div>
      </aside>
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-4 ml-auto">
              <span className="text-sm text-[#58595B]">{admin?.name}</span>
              <div className="w-10 h-10 rounded-full bg-[#075FA3] flex items-center justify-center text-white font-semibold">
                {admin?.name?.charAt(0) || 'A'}
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6">
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="startups" element={<StartupsManagement />} />
            <Route path="events" element={<EventsManagement />} />
            <Route path="advisors" element={<AdvisorsManagement />} />
            <Route path="registrations" element={<RegistrationsManagement />} />
            <Route path="bookings" element={<BookingsManagement />} />
            <Route path="queries" element={<QueriesManagement />} />
            <Route path="ideas" element={<IdeasManagement />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
